import { useCallback, useMemo, useState } from "react";
import { useWebSocketContext } from "../context/WebSocketContext";
import { EditWidgetRequest, EditWidgetWebSocketMessage } from "../types";
import { Widget } from "@/modules";

/**
 * Custom hook for edit-widget WebSocket operations.
 * Sends a widget + prompt to the backend and receives streamed step/done/error messages.
 */
export const useEditWidgetWebSocket = () => {
    const { state, sendMessage, clearMessages, isReady } = useWebSocketContext();
    const [isProcessing, setIsProcessing] = useState(false);
    const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);

    const messages = useMemo(() => {
        if (!currentSessionId) return [];
        return (state.messages as unknown as EditWidgetWebSocketMessage[]).filter(
            (msg) => msg.action === "edit-widget" && msg.data?.sessionId === currentSessionId
        );
    }, [state.messages, currentSessionId]);

    const clearSessionMessages = useCallback(() => {
        if (!currentSessionId) return;
        clearMessages(
            (message) => message.action === "edit-widget" && (message.data as unknown as EditWidgetWebSocketMessage["data"])?.sessionId === currentSessionId
        );
        setIsProcessing(false);
    }, [clearMessages, currentSessionId]);

    const sendRequest = useCallback(
        (widget: Widget, prompt: string) => {
            const sessionId = `edit-widget-${Date.now()}`;
            setCurrentSessionId(sessionId);
            setIsProcessing(true);

            // Strip data field — it contains large API response rows not needed by the backend
            const { data: _data, ...widgetWithoutData } = widget as Widget & { data?: unknown };

            const request: EditWidgetRequest = {
                action: "edit-widget",
                sessionId,
                widget: widgetWithoutData,
                prompt,
            };

            sendMessage(JSON.stringify({ ...request, timestamp: Date.now() }));
        },
        [sendMessage]
    );

    const resetProcessing = useCallback(() => setIsProcessing(false), []);

    return {
        isReady,
        isProcessing,
        error: state.error,
        messages,
        currentSessionId,
        sendRequest,
        clearSessionMessages,
        resetProcessing,
    };
};
